import { Injectable } from '@angular/core'
import {
  CashBreakAdvancedSettings,
  RootState,
  selectCashBreakAdvancedSettings,
} from '@helios/state-management'
import { TelemetryService } from '@helios/telemetry'
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { CashBreakAdvancedSettingsModalComponent } from '../../components/cash-break-advanced-settings-modal/cash-break-advanced-settings-modal.component'
import {
  AvailableAction,
  CashBreakInfo,
  CashBreakSuggestion,
  CashResolveRequest,
} from '../../models/copilot.model'
import { generateMarkdown } from '../../utils/markdown-generator'
import { map } from 'rxjs/operators'
import { HttpCopilotService } from '../copilot/http-copilot.service'

@Injectable({
  providedIn: 'root',
})
export class CashBreakService {
  constructor(
    private readonly store: Store<RootState>,
    private readonly modalService: NgbModal,
    private readonly telemetry: TelemetryService,
    private readonly httpCopilotService: HttpCopilotService,
  ) {
    this.store
      .select(selectCashBreakAdvancedSettings)
      .pipe(map(x => (this.cashBreakSettings = x)))
      .subscribe()
  }

  cashBreakSettings: CashBreakAdvancedSettings | undefined

  async createCashBreakSuggestions(cashBreak: CashBreakInfo) {
    await this.createCashBreakSuggestionsWithRequest(
      cashBreak,
      this.buildRequest(cashBreak, this.cashBreakSettings),
    )
  }

  async createCashBreakSuggestionsWithRequest(
    cashBreak: CashBreakInfo,
    request: CashResolveRequest,
  ) {
    this.telemetry.emitCustomEvent('heliosCashBreakSolver', {
      breakId: cashBreak.breakId,
      accountId: cashBreak.accountId,
      ultClientId: cashBreak.clientId ?? '',
      ultClientName: cashBreak.clientName ?? '',
    })

    window.copilot.open()
    window.copilot.startLoading()
    window.copilot.addAssistantMessage({
      content: 'Looking for Cash Break Solutions...',
    })
    let suggestions: CashBreakSuggestion[] = []
    try {
      suggestions = await this.httpCopilotService.getCashBreakSuggestions(
        request,
      )
      this.telemetry.emitCustomEvent('heliosCashBreakUniqueSolutionsPerBreak', {
        uniqueSolutions: suggestions.length,
        breakId: cashBreak.breakId,
        accountId: cashBreak.accountId,
        ultClientName: cashBreak.clientName ?? '',
        ultClientId: cashBreak.clientId ?? '',
      })
      this.handleCashBreakSuggestionResults(suggestions, cashBreak)
    } catch (error) {
      if (error.status == 400) {
        let errorMessage = error.error['errorMessage']
        errorMessage = errorMessage.substring(0, errorMessage.indexOf('('))
        window.copilot.addAssistantMessage({
          content: errorMessage,
        })
      } else
        window.copilot.addAssistantMessage({
          content: 'Failed to resolve cash break',
        })
    } finally {
      window.copilot.stopLoading()
      window.copilot.open()
    }
  }

  handleCashBreakSuggestionResults(
    results: CashBreakSuggestion[] | undefined,
    cashBreak: CashBreakInfo,
  ) {
    if (results == undefined) {
      window.copilot.addAssistantMessage({
        content: 'Failed to resolve cash break',
      })
    } else if (results.length == 0) {
      const promptMessage = `There were no solutions found for the cash difference break in account ${cashBreak.accountId} for assetId ${cashBreak.assetId}.`
      const suggestedActions = [
        {
          type: 'cashBreakAdvancedSettings',
          title: 'Try Again with Adjusted Settings',
          params: {
            cashBreakInfo: cashBreak,
          },
        },
      ] as AvailableAction[]

      window.copilot.addAssistantMessage({
        content: promptMessage,
        suggestedActions: suggestedActions,
      })
    } else {
      const breakId = cashBreak.breakId
      const acctId = cashBreak.accountId
      const promptMessage = generateMarkdown(results, cashBreak)
      const cwTranIds = results[0].cwTranToClear
        .map(x => x.tranId)
        .concat(results[0].cwTranToUnclear.map(x => x.tranId))
      const extTranIds = results[0].extTranToClear.map(x => x.extTranId)
      const suggestedActions = [
        {
          type: 'availableSolution',
          title: 'View All',
          params: {
            cashBreakSuggestions: results,
            breakId: breakId,
            acctId: acctId,
          },
        },
        {
          type: 'filterTransactionsById',
          title: 'Filter for Transactions',
          params: {
            cwTranIds: cwTranIds,
            extTranIds: extTranIds,
          },
        },
      ] as AvailableAction[]

      if (results[0].extTranToClear.length === 0) {
        const cwanSuggestion = {
          type: 'acceptSolution',
          title: 'Submit Suggestion',
          params: {
            cashBreakSuggestion: results[0],
            breakId: breakId,
            acctId: acctId,
          },
        }
        suggestedActions.push(cwanSuggestion as unknown as AvailableAction)
      }

      window.copilot.addAssistantMessage({
        content: promptMessage,
        suggestedActions: suggestedActions,
      })
    }
  }

  openCashBreakAdvancedSettingsModal(cashBreakInfo: CashBreakInfo) {
    const modalRef = this.modalService.open(
      CashBreakAdvancedSettingsModalComponent,
      {
        size: 'lg',
        modalDialogClass: 'my-1',
        keyboard: false,
      } as NgbModalOptions,
    )
    const result = modalRef.result as
      | Promise<CashBreakAdvancedSettings | null>
      | undefined
    if (!result) return null
    return result.then(
      settings =>
        settings
          ? this.buildRequest(
              cashBreakInfo,
              settings as unknown as CashBreakAdvancedSettings,
            )
          : null,
      (cancelMessage: string) => {},
    )
  }

  private buildRequest(
    cashBreak: CashBreakInfo,
    settings?: CashBreakAdvancedSettings,
  ): CashResolveRequest {
    return {
      securityId: cashBreak.securityId,
      accountId: cashBreak.accountId,
      source: cashBreak.source,
      difference: cashBreak.difference,
      excludeCashTransactions: settings?.clearedCashTransactions !== true,
      excludeMMFTransactions: settings?.clearedMmfTrades !== true,
      excludeExternalTransactions: settings?.unmatchedExtTransactions !== true,
      excludeUnlinkedTransactions: settings?.unmatchedUnclearedCwTx !== true,
      excludeIgnoredTransactions: settings?.ignoredExtTransactions !== true,
    } as CashResolveRequest
  }
}
